import { useEffect, useState } from "react";
import { Button } from "./ui/button";

const STEPS = [
  { sel: '[data-testid="client-switcher"]', title: "One brand at a time", body: "Everything you see — posts, calendar, insights — belongs to the brand chosen here. Switch anytime; nothing gets mixed up." },
  { sel: '[data-testid="nav-composer"]', title: "Where content is born", body: "Tell the co-pilot what you want to say. It drafts a version for every platform in your brand's voice — you stay the editor." },
  { sel: '[data-testid="nav-approvals"]', title: "Nothing goes out without a yes", body: "Every post stops here for human review. Approve it, ask for changes, or reject it. This is your safety net." },
  { sel: '[data-testid="nav-queue"]', title: "You always post it yourself", body: "Approved posts wait here, ready to copy and paste into each app. Celebre never publishes for you — that's a promise." },
  { sel: '[data-testid="credit-balance-badge"]', title: "Credits are the fuel", body: "Creating costs credits: text 1 · image 3 · article 10 · video 15. Failed generations are refunded automatically." },
  { sel: '[data-testid="tour-launch-button"]', title: "Help is always one click away", body: "Replay this tour anytime from here — and every page has a plain-language guide at the top." },
];

export default function GuidedTour({ open, onClose }) {
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);

  useEffect(() => { if (open) setIdx(0); }, [open]);

  useEffect(() => {
    if (!open) return;
    const measure = () => {
      const el = document.querySelector(STEPS[idx].sel);
      if (!el) {
        setRect(null);
        setIdx((i) => (i < STEPS.length - 1 ? i + 1 : i));
        return;
      }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height, right: r.right, bottom: r.bottom });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [open, idx]);

  if (!open || !rect) return null;
  const step = STEPS[idx];
  const cardW = 320;
  const toRight = rect.right + cardW + 32 < window.innerWidth && rect.left < window.innerWidth / 2;
  const cardStyle = toRight
    ? { top: Math.min(rect.top - 8, window.innerHeight - 220), left: rect.right + 16 }
    : { top: Math.min(rect.bottom + 14, window.innerHeight - 220), left: Math.min(Math.max(rect.left, 16), window.innerWidth - cardW - 16) };

  return (
    <div className="fixed inset-0 z-[100]" data-testid="guided-tour">
      <div className="absolute rounded-xl transition-all duration-300"
        style={{ top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12, boxShadow: "0 0 0 100vmax rgba(28,25,23,0.52)", border: "2px solid hsl(9,69%,62%)" }} />
      <div className="absolute bg-white border border-orange-900/10 rounded-2xl p-5 shadow-[0_18px_50px_rgba(28,25,23,0.25)] fade-up" style={{ ...cardStyle, width: cardW }}>
        <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[hsl(9,55%,50%)]">Step {idx + 1} of {STEPS.length}</p>
        <p className="font-display font-bold text-[17px] tracking-tight mt-1.5">{step.title}</p>
        <p className="text-[13px] text-[#6E6864] leading-relaxed mt-1.5">{step.body}</p>
        <div className="flex items-center justify-between mt-4">
          <button data-testid="tour-skip-button" onClick={onClose} className="text-[12px] font-medium text-[#6E6864] hover:text-[#2F2D2C] transition-colors">Skip tour</button>
          <div className="flex gap-2">
            {idx > 0 && (
              <Button data-testid="tour-back-button" size="sm" variant="outline" className="rounded-full h-8 px-4" onClick={() => setIdx(idx - 1)}>Back</Button>
            )}
            <Button data-testid="tour-next-button" size="sm" className="rounded-full h-8 px-4 bg-[hsl(9,69%,62%)] hover:bg-[hsl(8,54%,54%)]"
              onClick={() => (idx === STEPS.length - 1 ? onClose() : setIdx(idx + 1))}>
              {idx === STEPS.length - 1 ? "Finish" : "Next"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
